import http from 'src/http/fetchClient';
import CONTENT_TYPES from './contentTypes';

class DistributorService {
  getDistributors = async ({ page, size, orderBy, order, status, searchTerm }) => {
    try {
      const response = await http.get('/api/admin/distributors', {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors
        },
        data: {},
        params: {
          page,
          size,
          orderBy,
          order,
          status,
          searchTerm
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorById = async (id) => {
    try {
      const response = await http.get(`/api/admin/distributors/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  addDistributor = async ({ name, code, profileImage, countryCode, currencyCode, balanceThreshold, status }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('code', code);
      formData.append('image', profileImage);
      formData.append('countryCode', countryCode);
      formData.append('currencyCode', currencyCode);
      formData.append('balanceThreshold', balanceThreshold);
      formData.append('status', status);

      const response = await http.post('/api/admin/distributors', formData, {
        headers: {
          'Content-Type': CONTENT_TYPES.multipart_formdata
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateDistributor = async ({ id, name, code, profileImage, countryCode, currencyCode, balanceThreshold, status }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('code', code);
      formData.append('image', profileImage);
      formData.append('countryCode', countryCode);
      formData.append('currencyCode', currencyCode);
      formData.append('balanceThreshold', balanceThreshold);
      formData.append('status', status);

      const response = await http.put(`/api/admin/distributors/${id}`, formData, {
        headers: {
          'Content-Type': CONTENT_TYPES.multipart_formdata
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  deleteDistributor = async (id) => {
    try {
      return await http.delete(`/api/admin/distributors/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors
        },
        data: {}
      });
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorContactById = async (id) => {
    try {
      const response = await http.get(`/api/admin/distributors/${id}/contact`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_contact
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateContactByDistributorId = async ({ id, website, email, dialCode, phone, address }) => {
    try {
      const response = await http.put(
        `/api/admin/distributors/${id}/contact`,
        {
          website,
          email,
          dialCode,
          phone,
          address
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_contact
          }
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorSettingsById = async (id) => {
    try {
      const response = await http.get(`/api/admin/distributors/${id}/settings`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_settings
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateSettingsByDistributorId = async ({
    id,
    webhookUrl,
    balanceNotificationEmail,
    balanceNotificationWebhookUrl,
    balanceNotificationSlackWebhookUrl,
    sendFulfilmentEmails
  }) => {
    try {
      const response = await http.put(
        `/api/admin/distributors/${id}/settings`,
        {
          webhookUrl,
          balanceNotificationEmail,
          balanceNotificationWebhookUrl,
          balanceNotificationSlackWebhookUrl,
          sendFulfilmentEmails
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_settings
          }
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getCredentialsByDistributorId = async (id) => {
    try {
      const response = await http.get(`/api/admin/distributors/${id}/credentials`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_credentials
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  addCredentialsByDistributorId = async ({ id, type }) => {
    try {
      const response = await http.post(
        `/api/admin/distributors/${id}/credentials`,
        { type },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_credentials
          }
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getPermittedDistributors = async () => {
    try {
      const response = await http.get('/api/admin/distributors/permitted', {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorProducts = async ({ distributorId, page, size, orderBy, order, searchTerm, countryCode }) => {
    try {
      const response = await http.get(`/api/admin/distributors/${distributorId}/products`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_products
        },
        data: {},
        params: {
          page,
          size,
          orderBy,
          order,
          searchTerm,
          countryCode
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorProductBundles = async ({
    distributorId,
    productId,
    page,
    size,
    orderBy,
    order,
    searchTerm,
    countryCode
  }) => {
    try {
      const response = await http.get(`/api/admin/distributors/${distributorId}/products/bundles`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_products
        },
        data: {},
        params: {
          productId,
          page,
          size,
          orderBy,
          order,
          searchTerm,
          countryCode
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  saveDistributorProduct = async (distributorId, productId, productType, bundles) => {
    try {
      return await http.post(
        `/api/admin/distributors/${distributorId}/products/${productId}`,
        {
          productType,
          bundles
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_products
          }
        }
      );
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  deleteDistributorProduct = async (distributorId, distributorProductId) => {
    try {
      return await http.delete(`/api/admin/distributors/${distributorId}/products/${distributorProductId}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_products
        },
        data: {}
      });
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorBalanceTopUps = async ({ distributorId, page, size, orderBy, order, status, type }) => {
    try {
      const response = await http.get(`/api/admin/distributors/${distributorId}/balance/top-ups`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_balance
        },
        data: {},
        params: {
          page,
          size,
          orderBy,
          order,
          status,
          type
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getDistributorBalanceTopUpById = async (distributorId, id) => {
    try {
      const response = await http.get(`/api/admin/distributors/${distributorId}/balance/top-ups/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.distributors_balance
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  createDistributorBalanceTopUpRequest = async (distributorId, amount, notes, type) => {
    try {
      return await http.post(
        `/api/admin/distributors/${distributorId}/balance/top-ups`,
        {
          amount,
          notes,
          type
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_balance
          }
        }
      );
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateDistributorBalanceTopUpRequest = async (distributorId, id, amount, notes, type) => {
    try {
      return await http.put(
        `/api/admin/distributors/${distributorId}/balance/top-ups/${id}`,
        {
          amount,
          notes,
          type
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_balance
          }
        }
      );
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  approveDistributorBalanceTopUpRequest = async (distributorId, id, notes) => {
    try {
      return await http.post(
        `/api/admin/distributors/${distributorId}/balance/top-ups/${id}/approve`,
        {
          notes
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_balance
          }
        }
      );
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  rejectDistributorBalanceTopUpRequest = async (distributorId, id, notes) => {
    try {
      return await http.post(
        `/api/admin/distributors/${distributorId}/balance/top-ups/${id}/reject`,
        {
          notes
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.distributors_balance
          }
        }
      );
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  resendDistributorWebhook = async (orderItemId) => {
    try {
      const response = await http.post(
        `/api/admin/orders/distributors/webhook/resend/${orderItemId}`,
        {},
        {
          headers: {
            'Content-Type': CONTENT_TYPES.order_distributor
          }
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  exportDistributorProducts = async (distributorId, productIds) => {
    try {
      const response = await http.post(
        '/api/admin/distributors/products/bundles/export',
        {
          distributorId,
          productIds
        },
        {
          headers: {
            'Content-Type': 'application/vnd.carry1st.products.distributorproduct+json',
            Accept: 'application/vnd.carry1st.products.distributorproduct+json'
          },
          responseType: 'blob'
        }
      );

      // Handle file download
      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'distributor-products-export.csv';

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch?.[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '').replace(' ', '_');
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      return response;
    } catch (error) {
      throw new Error(error.response?.data?.errorMessage || 'Failed to export products');
    }
  };
}

const distributorService = new DistributorService();

export default distributorService;
